// Public landing page (#/, no sign-in) — redesigned after the reference composition:
// hero (copy + networking-lab illustration) → benefits row → "Why Learn with RouteZero?"
// learning-path carousel → "How RouteZero Works" explainer video (three steps + six benefits,
// src/ui/explainer-video.js) → from theory to practice → pricing (free access) → dark call to action.
// It holds no state of its own: learning paths come from the real curriculum and the
// visitor's guest Support Desk summary; nothing here marks anything complete or unlocked.
import { h } from './h.js';
import { BRAND, symbolSrc } from './brand.js';
import { registrationCheckpoint } from './checkpoint.js';
import { curriculum } from '../core/curriculum.js';
import { carousel, prefersReducedMotion } from './carousel.js';
import { explainerVideo } from './explainer-video.js';
import { icon } from './icons.js';
import { scrollToSection } from './views.js';
import { testimonialsSection } from './testimonials-views.js';

const THEORY_ONLY = [
  'Finding it difficult to organize videos, articles, notes and practice resources into a consistent sequence.',
  'Knowing commands in theory but lacking practice applying them to a realistic problem.',
  'Having limited opportunities to investigate a simulated network failure safely.',
  'Not knowing which topic to study next.',
  'Finding it difficult to measure practical progress across separate resources.',
  'Having little experience documenting incidents and writing professional escalation notes.',
];
const WITH_RZ = [
  'A structured progression from beginner IT support toward networking.',
  'Interactive troubleshooting tickets and realistic scenarios.',
  'Network topology diagrams and device consoles.',
  'CLI-based investigation and configuration practice.',
  'Feedback and verification based on simulated state where supported.',
  'A consistent place to track progress after registration.',
  'A long-term roadmap toward networking and future security learning.',
];

// Beginner-friendly one-liners for each domain (status comes from the curriculum).
const STAGE_COPY = {
  d01: 'Learn how networks move data, including models, Ethernet, IP addressing and troubleshooting.',
  d02: 'Connect devices inside a building using switches, VLANs, trunks, spanning tree and EtherChannel.',
  d03: 'Learn how routers reach other networks with static routes, OSPF, EIGRP, BGP and route selection.',
  d04: 'Connect sites using WAN links, redundant paths, QoS and VPN concepts.',
  d05: 'Learn DHCP, DNS, NAT, gateway redundancy, NTP, Syslog and SNMP.',
  d06: 'Secure device access with SSH, AAA, RADIUS and TACACS+, then protect traffic with ACLs and port security.',
  d07: 'Operate the network: backups and restores, IOS upgrades, CDP/LLDP, monitoring, documentation and change control.',
};

/**
 * Learning-path model — presentation only. Status and counts come from the real curriculum
 * (implemented units) and the visitor's guest Support Desk summary.
 */
export function journeyStages(guestSummary = { done: 0, total: 5, complete: false }) {
  const g = guestSummary;
  const stages = [{
    n: 1, id: 'support', icon: 'headset', title: 'Level 1 Support Desk', desc: 'Solve five beginner IT support tickets: read the user’s problem, collect evidence, resolve or escalate.',
    state: g.complete ? 'complete' : 'current', badge: g.complete ? '✓ COMPLETE' : g.done > 0 ? '▶ IN PROGRESS' : '▶ FREE · START HERE',
    units: g.total, unitLabel: 'tickets', done: g.done, note: `${g.total} tickets · ${g.done} complete`,
    href: '#/support', action: g.done > 0 && !g.complete ? 'Continue the free Support Desk' : 'Open the free Support Desk',
  }];
  const ICONS = { d01: 'network', d02: 'switch', d03: 'router', d04: 'globe', d05: 'server', d06: 'lock', d07: 'gear' };
  curriculum.domains.forEach((d, i) => {
    const built = d.units.filter((u) => u.implemented).length;
    const live = built > 0 && built === d.units.length;
    stages.push(live
      ? { n: i + 2, id: d.id, icon: ICONS[d.id] || 'network', title: d.title, desc: STAGE_COPY[d.id] || d.tagline, state: i === 0 && g.complete ? 'next' : 'account', badge: i === 0 && g.complete ? '→ NEXT · FREE ACCOUNT' : 'FREE ACCOUNT', units: built, unitLabel: 'learning units', done: 0, note: `${built} learning units · 0 complete`, href: '#/signup', action: `Create a free account to start ${d.title}` }
      : { n: i + 2, id: d.id, icon: ICONS[d.id] || 'lock', title: d.title, desc: STAGE_COPY[d.id] || d.tagline, state: 'locked', badge: '🔒 IN DEVELOPMENT', units: d.units.length, unitLabel: 'units planned', done: 0, note: `${d.units.length} units planned · not available yet`, href: null, action: null });
  });
  stages.push({ n: stages.length + 1, id: 'security', icon: 'firewall', title: 'Future Security Labs', desc: 'Preview firewall and network security learning. This is not an active curriculum.', state: 'soon', badge: 'COMING SOON — FIREWALLS · NETWORK SECURITY', units: 0, done: 0, note: 'Roadmap preview', href: null, action: null });
  return stages;
}

const pad = (n) => String(n).padStart(2, '0');
const head = ({ eyebrow, title, sub, id, extra }) => h('div', { class: 'rz-sec-head' },
  h('div', {}, h('span', { class: 'rz-eyebrow' }, eyebrow), h('h2', { id }, ...title), sub ? h('p', {}, sub) : null), extra || null);

/** "Why Learn with RouteZero?" — the learning paths as a carousel (real curriculum data). */
function learningPaths(guestSummary) {
  const stages = journeyStages(guestSummary);
  const cards = stages.map((st) => {
    const pct = st.units ? Math.round((st.done / st.units) * 100) : 0;
    return h('article', { class: `rz-path-card ${st.state}`, 'data-testid': `journey-${st.id}`, 'data-state': st.state },
      h('div', { class: 'rz-path-top' },
        h('span', { class: `rz-orb o${(st.n % 4) + 1}` }, icon(st.icon, { size: 20 })),
        h('span', { class: 'rz-num-pill', 'aria-hidden': 'true' }, pad(st.n)),
        st.href ? h('a', { class: 'rz-path-go', href: st.href, 'data-testid': `journey-${st.id}-go`, 'aria-label': st.action }, icon('arrowRight', { size: 16 }))
          : h('span', { class: 'rz-path-go is-off', role: 'img', 'aria-label': st.state === 'soon' ? 'Not open yet' : 'Not available yet', title: st.state === 'soon' ? 'Not open yet' : 'Not available yet' }, icon('lock', { size: 15 }))),
      h('h3', {}, h('span', { class: 'sr-only' }, `Stage ${st.n}: `), st.title),
      h('p', {}, st.desc),
      h('div', { class: 'rz-path-foot rz-stage-foot' },
        h('div', { class: 'rz-path-meta' }, h('small', {}, st.note), h('span', { class: 'rz-stage-status' }, st.badge)),
        st.units ? h('div', { class: 'rz-bar', role: 'progressbar', 'aria-label': `${st.title} progress`, 'aria-valuemin': 0, 'aria-valuemax': st.units, 'aria-valuenow': st.done }, h('span', { style: { width: `${pct}%` } })) : h('div', { class: 'rz-bar is-empty', 'aria-hidden': 'true' })));
  });
  const car = carousel({ id: 'journey-carousel', label: 'Learning paths', slides: cards, interval: 7000, sideArrows: true, slideLabel: (i, n) => `Stage ${i + 1} of ${n}` });
  return h('section', { class: 'rz-section rz-paths', id: 'rz-paths', 'aria-labelledby': 'rz-paths-title', 'data-testid': 'landing-journey' },
    head({ eyebrow: 'REASONS TO CHOOSE ROUTEZERO', id: 'rz-paths-title', title: ['Why Learn with ', h('span', { class: 'rz-grad' }, 'RouteZero'), '?'], sub: 'Build practical skills, one mission at a time.', extra: car.controls }),
    car.el,
    h('p', { class: 'rz-journey-cta' }, 'Registered learners follow these paths on the interactive Philippine Mission Map. ',
      h('a', { href: '#/signup', 'data-testid': 'journey-map-cta' }, 'Create a free account to open the Mission Map →')));
}

/**
 * Hero headline: each word fades in from a light blur (opacity, filter, transform), staggered
 * by --i; the eyebrow, subcopy, buttons and note follow after the final word. Runs once when
 * the hero is in view, then drops will-change. The h1 keeps its full text as aria-label.
 * The last line keeps one continuous gradient across its words (positioned per word).
 */
const WORD_STAGGER = 90;
function heroHeadline(lines) {
  let i = 0;
  const words = [];
  const h1 = h('h1', { id: 'rz-hero-title', class: 'rz-blur-h1', 'aria-label': lines.join(' ') },
    lines.map((line, li) => h('span', { class: `rz-hl${li === lines.length - 1 ? ' rz-hl-grad' : ''}`, 'aria-hidden': 'true' },
      line.split(' ').flatMap((w, k) => {
        const el = h('span', { class: `rz-bw${li === lines.length - 1 ? ' rz-grad' : ''}`, style: `--i:${i++}` }, w);
        words.push(el);
        return k ? [' ', el] : [el];
      }))));
  h1.style.setProperty('--words', i);
  h1.__words = words;
  return h1;
}
/** One continuous gradient over the words of a line (each word shows its slice). */
function alignGradient(h1) {
  for (const line of h1.querySelectorAll('.rz-hl-grad')) {
    const ws = [...line.querySelectorAll('.rz-bw')];
    if (!ws.length) continue;
    const left = Math.min(...ws.map((w) => w.offsetLeft));
    const width = Math.max(...ws.map((w) => w.offsetLeft + w.offsetWidth)) - left;
    for (const w of ws) { w.style.backgroundSize = `${width}px 100%`; w.style.backgroundPosition = `${left - w.offsetLeft}px 0`; }
  }
}
/** Play the headline sequence once (on load / when the hero scrolls into view). */
function playHero(hero) {
  const h1 = hero.querySelector('.rz-blur-h1');
  if (!h1) return;
  hero.classList.add('is-armed');
  hero.style.setProperty('--words', h1.style.getPropertyValue('--words'));
  const align = () => alignGradient(h1);
  requestAnimationFrame(align);
  document.fonts?.ready?.then(align);
  window.addEventListener('resize', align, { passive: true });
  const start = () => {
    align();
    hero.classList.add('is-in');
    // will-change only for the duration of the sequence
    const last = hero.querySelector('.rz-free-note');
    const done = () => hero.classList.add('is-done');
    if (prefersReducedMotion() || !last) done();
    else { last.addEventListener('animationend', done, { once: true }); setTimeout(done, 2600); }
  };
  if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') { start(); return; }
  const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); start(); } }, { threshold: 0.2 });
  io.observe(hero);
}

/**
 * Hero illustration: a learner dashboard mock that assembles itself once when the hero
 * scrolls into view (sidebar → header → stat cards → charts; the line draws, the bars grow).
 * Drawn in HTML/SVG; decorative only (aria-hidden), the numbers are illustrative.
 * Motion: transform, opacity and stroke-dashoffset only. Reduced motion: final state, no movement.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';
const svgEl = (tag, attrs = {}) => { const e = document.createElementNS(SVG_NS, tag); for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v); return e; };
function heroArt() {
  const piece = (i, cls, ...kids) => h('div', { class: `rz-dp ${cls}`, style: `--i:${i}` }, ...kids);
  const stat = (i, label, value, delta) => piece(i, 'rz-dw rz-dstat', h('small', {}, label), h('strong', {}, value), h('em', {}, delta));
  // line-area chart: XP earned over the last seven days
  const LINE = 'M0 34 C10 31 16 22 28 23 S46 30 58 18 S82 8 100 6';
  const chart = svgEl('svg', { viewBox: '0 0 100 40', preserveAspectRatio: 'none', 'aria-hidden': 'true', focusable: 'false' });
  const defs = svgEl('defs');
  const grad = svgEl('linearGradient', { id: 'rz-dash-area', x1: '0', y1: '0', x2: '0', y2: '1' });
  grad.append(svgEl('stop', { offset: '0', 'stop-color': '#8B5CF6', 'stop-opacity': '.28' }), svgEl('stop', { offset: '1', 'stop-color': '#8B5CF6', 'stop-opacity': '0' }));
  defs.append(grad);
  chart.append(defs, ...[10, 20, 30].map((y) => svgEl('line', { class: 'rz-dgrid', x1: 0, x2: 100, y1: y, y2: y })),
    svgEl('path', { class: 'rz-darea', d: `${LINE} V40 H0z` }), svgEl('path', { class: 'rz-dline', pathLength: '1', d: LINE }));
  const BARS = [['D1', 92], ['D2', 78], ['D3', 64], ['D4', 70], ['D5', 52], ['D6', 38], ['D7', 22]];
  const dash = h('div', { class: 'rz-dash', 'data-testid': 'hero-dashboard' },
    h('div', { class: 'rz-dash-bar' }, h('i'), h('i'), h('i'), h('span', { class: 'rz-dash-url' }, 'routezero · mission control')),
    h('div', { class: 'rz-dash-body' },
      piece(0, 'rz-dside',
        h('img', { src: symbolSrc(64), alt: '', width: 18, height: 18 }),
        ...['Missions', 'Labs', 'Tickets', 'Map', 'Profile'].map((t, k) => h('span', { class: k === 0 ? 'on' : '' }, h('i'), h('b', {}, t)))),
      h('div', { class: 'rz-dmain' },
        piece(1, 'rz-dhead', h('div', {}, h('small', {}, 'Welcome back'), h('b', {}, 'Mission Control')), h('span', { class: 'rz-dlvl' }, 'LV 12'), h('span', { class: 'rz-davatar' })),
        h('div', { class: 'rz-dstats' },
          stat(2, 'Missions done', '42', '+3 this week'),
          stat(3, 'XP earned', '4,250', '+620'),
          stat(4, 'Faults fixed', '128', '96% first try')),
        h('div', { class: 'rz-dcharts' },
          piece(5, 'rz-dw rz-dline-card', h('div', { class: 'rz-dcap' }, h('b', {}, 'XP · last 7 days'), h('small', {}, 'Mon – Sun')), h('div', { class: 'rz-dsvg' }, chart)),
          piece(6, 'rz-dw rz-dbar-card', h('div', { class: 'rz-dcap' }, h('b', {}, 'Labs by domain')),
            h('div', { class: 'rz-dbars' }, BARS.map(([d, v], k) => h('span', { class: `rz-dcol${k === 5 ? ' on' : ''}` }, h('u', { style: `height:${v}%;--b:${k}` }), h('small', {}, d)))))))));
  // Arm (hide) the pieces only once JS runs, then play once when the hero is in view.
  dash.classList.add('is-armed');
  const play = () => dash.classList.add('is-in');
  if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') play();
  else {
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { play(); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(dash);
  }
  return h('div', { class: 'rz-hero-art', 'aria-hidden': 'true', 'data-testid': 'hero-art' },
    h('span', { class: 'rz-blob b1' }), h('span', { class: 'rz-blob b2' }), h('span', { class: 'rz-blob b3' }),
    dash,
    h('span', { class: 'rz-chip c1' }, icon('shield', { size: 26 })),
    h('span', { class: 'rz-hero-note', 'data-testid': 'hero-illustrative' }, 'Illustrative preview · sample numbers'),
    h('div', { class: 'rz-hero-card' }, h('b', {}, 'Build Skills'), h('b', {}, 'Fix Real Faults'), h('b', {}, 'Advance')));
}

export function publicLandingView(guestSupport, { testimonials = null } = {}) {
  const s = guestSupport.summary();
  const started = s.done > 0 || Object.keys(guestSupport.raw().tickets || {}).length > 0;
  const BENEFITS = [
    ['monitor', 'Hands-on Labs', 'Practice in your browser'],
    ['path', 'Structured Path', `Support Desk + ${curriculum.domains.length} network domains`],
    ['shield', 'Firewalls & Security', 'Security labs coming soon'],
    ['chart', 'Real-World Scenarios', 'Troubleshooting tickets'],
  ];
  const pricing = registrationCheckpoint({ testid: 'landing-checkpoint', heading: 'Free to start. Free to register.' });
  pricing.id = 'rz-pricing';
  const main = h('main', { class: 'page rz-landing', 'data-testid': 'public-landing', 'data-reading': 'article' },
    // HERO
    h('section', { class: 'rz-hero', 'aria-labelledby': 'rz-hero-title', 'data-testid': 'landing-hero' },
      h('div', { class: 'rz-hero-copy' },
        h('span', { class: 'rz-eyebrow' }, 'YOUR PATH TO A CAREER IN NETWORKING & SECURITY'),
        heroHeadline(['Learn Networking.', 'Solve Real Problems.', 'Build Your Future.']),
        h('p', { class: 'rz-hero-lede' }, h('b', {}, 'Interactive labs, realistic support tickets'), ' and guided learning paths — from your first helpdesk ticket to switching, routing, WAN and infrastructure services. CCNA-level networking topics, all in your browser.'),
        h('div', { class: 'rz-hero-cta' },
          h('a', { class: 'btn primary rz-cta-main', href: '#/support', 'data-testid': 'start-free-mission' }, started ? `Continue Learning · ${s.done}/${s.total}` : 'Start Learning', icon('arrowRight', { size: 18 })),
          h('button', { class: 'btn rz-btn-soft rz-ring-btn', type: 'button', 'data-testid': 'watch-demo', onclick: () => scrollToSection('rz-how') }, h('i', { class: 'rz-ring-spin', 'aria-hidden': 'true' }), h('span', { class: 'rz-play-dot' }, icon('play', { size: 12 })), 'Watch Demo')),
        h('p', { class: 'rz-free-note', 'data-testid': 'landing-free-note' }, 'Free to start. No account required for the introductory Support Desk experience. ', h('span', { class: 'rz-kicker-inline' }, 'FREE LEVEL 1 SUPPORT DESK'))),
      heroArt()),
    // BENEFITS ROW
    h('ul', { class: 'rz-benefits-row', 'data-testid': 'landing-benefits', 'aria-label': 'What RouteZero offers' },
      BENEFITS.map(([ic, t, d]) => h('li', {}, h('span', { class: 'rz-tile' }, icon(ic, { size: 22 })), h('div', {}, h('b', {}, t), h('small', {}, d))))),
    learningPaths(s),
    // HOW IT WORKS + WHAT YOU GET — one compact explainer video (src/ui/explainer-video.js)
    explainerVideo(),
    // FROM THEORY TO PRACTICE
    h('section', { class: 'rz-section', id: 'rz-theory', 'aria-labelledby': 'rz-theory-title', 'data-testid': 'landing-theory' },
      head({ eyebrow: 'FROM THEORY TO PRACTICE', id: 'rz-theory-title', title: ['From Theory to ', h('span', { class: 'rz-grad' }, 'Practice')], sub: 'Understanding a concept is important. Learning how to apply it is just as important.' }),
      h('div', { class: 'rz-compare' },
        h('div', { class: 'panel rz-compare-col', 'data-testid': 'theory-alone' },
          h('h3', {}, 'Studying through disconnected resources alone'),
          h('p', { class: 'small muted' }, 'Learners may run into challenges such as:'),
          h('ul', {}, THEORY_ONLY.map((x) => h('li', {}, x))),
          h('p', { class: 'rz-fair-note', 'data-testid': 'theory-disclaimer' }, 'Books, official documentation, videos, courses and other platforms remain valuable learning resources. These are possible challenges, not disadvantages everyone will experience.')),
        h('div', { class: 'panel rz-compare-col rz-compare-rz', 'data-testid': 'theory-routezero' },
          h('h3', {}, 'Learning with RouteZero'),
          h('p', { class: 'small muted' }, 'What the platform supports today:'),
          h('ul', {}, WITH_RZ.map((x) => h('li', {}, x))))),
      h('p', { class: 'rz-closing', 'data-testid': 'theory-closing' }, 'Learn the concepts from trusted resources. Use RouteZero to put your knowledge into practice.')),
    // WHAT LEARNERS SAY — approved testimonials only; hidden while there are none
    testimonialsSection(testimonials),
    // PRICING — free access and the free account
    pricing,
    // FINAL CTA + FOOTER (dark)
    h('section', { class: 'rz-cta', 'aria-labelledby': 'rz-cta-title', 'data-testid': 'landing-cta' },
      h('div', { class: 'rz-cta-inner' },
        h('div', { class: 'rz-cta-copy' },
          h('span', { class: 'rz-eyebrow on-dark-pill' }, 'READY TO START?'),
          h('h2', { id: 'rz-cta-title' }, 'Your Next Mission Awaits.'),
          h('p', {}, 'Join learners building real skills, one ticket and one network at a time.')),
        h('div', { class: 'rz-cta-actions' },
          h('a', { class: 'btn primary', href: '#/support', 'data-testid': 'cta-start' }, 'Start Learning', icon('arrowRight', { size: 16 })),
          h('a', { class: 'btn rz-btn-outline-dark', href: '#/', 'data-testid': 'cta-paths', onclick: (e) => { e.preventDefault(); scrollToSection('rz-paths'); } }, 'View Learning Path')),
        h('div', { class: 'rz-cta-art', 'aria-hidden': 'true' },
          h('span', { class: 'rz-cloud' }),
          h('div', { class: 'rz-mini-laptop' }, h('div', { class: 'rz-ml-screen' }, h('img', { src: symbolSrc(192), alt: '', width: 44, height: 44 })), h('div', { class: 'rz-ml-base' }))))));
  // arriving from another page via a section link (Learning Path / How it Works / Pricing);
  // two frames, so the pinned scroll tracks (set up on the first frame) have measured first
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const id = window.__rzScrollTo; window.__rzScrollTo = null;
    if (id && main.isConnected) document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }));
  playHero(main.querySelector('.rz-hero'));
  // The page (main) is a curtain over a fixed footer: scrolling to the end lifts it away.
  return h('div', { class: 'rz-curtain-page' }, main, curtainFooter());
}

/**
 * Curtain-reveal footer. Sits fixed under the page (z-index 0); main (z-index 1, opaque,
 * rounded bottom, margin-bottom = footer height) scrolls up to reveal it — no JS needed for
 * the reveal. A rAF-throttled scroll listener adds a small parallax (translateY 22px → 0).
 * It follows main in the DOM (normal reading order); focusing a footer link scrolls to the end.
 * Reduced motion / short screens: an ordinary static footer (see 21-curtain-footer.css).
 */
function curtainFooter() {
  const link = (href, text, section) => h('a', { href, ...(section ? { onclick: (e) => { e.preventDefault(); scrollToSection(section); } } : {}) }, text);
  const inner = h('div', { class: 'rz-curtain-inner' },
    h('div', { class: 'rz-curtain-top' },
      h('div', { class: 'rz-curtain-brand' },
        h('img', { src: symbolSrc(192), alt: '', width: 40, height: 40 }),
        h('p', { class: 'rz-curtain-title' }, 'Start with zero.', h('br'), 'Engineer your future.'),
        h('a', { class: 'rz-curtain-cta', href: '#/support' }, 'Open the free Support Desk', icon('arrowRight', { size: 16 }))),
      h('nav', { class: 'rz-curtain-nav', 'aria-label': 'Footer' },
        h('div', {}, h('h2', {}, 'Learn'), link('#/support', 'Free Support Desk'), link('#/', 'Learning Path', 'rz-paths'), link('#/', 'How it Works', 'rz-how')),
        h('div', {}, h('h2', {}, 'Account'), link('#/signup', 'Create free account'), link('#/login', 'Sign in'), link('#/', 'Pricing', 'rz-pricing')))),
    h('div', { class: 'rz-curtain-legal' },
      h('div', {}, h('b', {}, BRAND.name), ' · ', BRAND.tagline),
      h('small', {}, 'RouteZero is a learning and practice platform. It is not an official Cisco certification provider and does not guarantee employment or certification. Simulated labs for learning; Cisco and IOS are trademarks of Cisco Systems, Inc. RouteZero Technologies is a fictional training company.')));
  const foot = h('footer', { class: 'rz-curtain', 'data-testid': 'landing-footer', 'data-cleanup': '' }, inner);
  let raf = 0;
  const update = () => {
    raf = 0;
    if (!foot.isConnected) return;
    const fh = foot.offsetHeight || 1;
    const left = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
    const q = Math.min(1, Math.max(0, 1 - left / fh)); // 0 → footer hidden, 1 → fully revealed
    foot.style.setProperty('--reveal', q.toFixed(3));
  };
  const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  requestAnimationFrame(update);
  // keyboard users: tabbing into the footer brings it into view
  foot.addEventListener('focusin', () => {
    if (getComputedStyle(foot).position === 'fixed') window.scrollTo({ top: document.documentElement.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });
  foot.__cleanup = () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  return foot;
}
